import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { getMainTabId, isEditOnboardingRoute } from "./mainTabRoutes";
import { isProfileSubpageRoute } from "./profileSubpageNav";
import {
  getOnboardingResumePath,
  isOnboardingIncomplete,
} from "../features/onboarding/onboardingSession";
import { AUTH_EXIT_EVENT, AUTH_SLIDE_MS } from "../features/auth/authEntrySlide";

const TabSwitchContext = createContext(null);

const TAB_TO_PATH = {
  home: "/",
  reels: "/reels",
  profile: "/profile-details",
};

const AUTH_PATHS = new Set(["/auth", "/signin", "/register"]);

export function setReelsRouteClass(isReels) {
  document.documentElement.classList.toggle("route-reels", isReels);
}

export function TabSwitchProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const routeTab = getMainTabId(location.pathname);
  const [visualTab, setVisualTab] = useState(routeTab ?? "home");
  const [navTab, setNavTab] = useState(routeTab ?? "home");
  const visualTabRef = useRef(visualTab);
  const userTabOverrideRef = useRef(null);

  visualTabRef.current = visualTab;

  // Deep link / reload / post-onboarding — keep visualTab aligned with the URL.
  useLayoutEffect(() => {
    if (!routeTab) {
      if (isEditOnboardingRoute(location.pathname, location.state)) {
        visualTabRef.current = "profile";
        setVisualTab("profile");
        setNavTab("profile");
      } else if (isProfileSubpageRoute(location.pathname, location.state)) {
        visualTabRef.current = "profile";
        setVisualTab("profile");
        setNavTab("profile");
      }
      return;
    }
    if (userTabOverrideRef.current !== null) {
      if (routeTab !== userTabOverrideRef.current) return;
      userTabOverrideRef.current = null;
    }
    visualTabRef.current = routeTab;
    setVisualTab(routeTab);
    setNavTab(routeTab);
    setReelsRouteClass(routeTab === "reels");
  }, [location.pathname, location.state, routeTab]);

  const switchTab = useCallback(
    (tabId, { requiresAuth = false, isAuthenticated = true, user = null, instant = false } = {}) => {
      const to = TAB_TO_PATH[tabId];
      if (!to) return;

      if (requiresAuth && !isAuthenticated) {
        const fromTab = visualTabRef.current;
        setReelsRouteClass(fromTab === "reels");
        navigate("/auth", { state: { navTab: tabId, fromTab } });
        return;
      }

      if (tabId === "profile" && isAuthenticated && isOnboardingIncomplete(isAuthenticated, user)) {
        navigate(getOnboardingResumePath(), { replace: true });
        return;
      }

      setReelsRouteClass(tabId === "reels");

      const commitTab = () => {
        userTabOverrideRef.current = tabId;
        flushSync(() => {
          visualTabRef.current = tabId;
          setVisualTab(tabId);
          setNavTab(tabId);
        });
      };

      if (instant) {
        flushSync(commitTab);
        navigate(to, { replace: true, state: { instantTab: true } });
        return;
      }

      if (AUTH_PATHS.has(location.pathname)) {
        const fromTab = location.state?.fromTab;
        if ((fromTab === "home" || fromTab === "reels") && tabId === fromTab) {
          commitTab();
          window.dispatchEvent(new CustomEvent(AUTH_EXIT_EVENT));
          window.setTimeout(() => {
            navigate(to, { replace: true });
          }, AUTH_SLIDE_MS);
          return;
        }
      }

      commitTab();

      if (location.pathname !== to) {
        navigate(to, {
          replace: false,
          state: undefined,
        });
      }
    },
    [location.pathname, navigate],
  );

  const value = useMemo(
    () => ({
      visualTab,
      navTab,
      switchTab,
      routeTab,
    }),
    [visualTab, navTab, switchTab, routeTab],
  );

  return <TabSwitchContext.Provider value={value}>{children}</TabSwitchContext.Provider>;
}

export function useTabSwitch() {
  const ctx = useContext(TabSwitchContext);
  if (!ctx) {
    throw new Error("useTabSwitch must be used within TabSwitchProvider");
  }
  return ctx;
}
